/**
 * @fileoverview
 * Notification WebSocket Service - Real-time in-app notifications via /ws/notifications.
 * Connects with token, subscribes, and forwards events to InAppNotificationStore.
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable, inject, OnDestroy, NgZone } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { TokenService } from '@core/authentication/services/token.service';
import { InAppNotificationStore } from '@core/state/stores/in-app-notification.store';
import { notificationEventToItem } from '@core/state/adapters/in-app-notification.adapter';
import type { NotificationEventPayload } from '@core/state/models/in-app-notification.model';

@Injectable({
  providedIn: 'root',
})
export class NotificationWebSocketService implements OnDestroy {
  private readonly tokenService = inject(TokenService);
  private readonly store = inject(InAppNotificationStore);
  private readonly ngZone = inject(NgZone);

  private socket: Socket | null = null;

  ngOnDestroy(): void {
    this.disconnect();
  }

  connect(): void {
    if (this.socket?.connected) return;
    if (!environment.features?.enableWebSocket) return;
    if (environment.features?.enableNotifications === false) return;

    const token = this.tokenService.getToken();
    if (!token) return;

    let baseUrl = environment.wsUrl ?? environment.apiUrl.replace('/api/v1', '');
    baseUrl = baseUrl.replace(/\/ws$/, '').replace(/^ws:\/\//, 'http://').replace(/^wss:\/\//, 'https://');

    this.socket = io(`${baseUrl}/ws/notifications`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 2000,
      timeout: 10000,
    });

    this.socket.on('connect', () => {
      this.socket?.emit('subscribe:notifications');
    });

    this.socket.on('notification', (payload: NotificationEventPayload) => {
      const item = notificationEventToItem(payload);
      this.ngZone.run(() => this.store.add(item));
    });

    this.socket.on('notification:broadcast', (payload: NotificationEventPayload) => {
      const item = notificationEventToItem(payload);
      this.ngZone.run(() => this.store.add(item));
    });

    this.socket.on('disconnect', () => {
      // No clear here; clear only on explicit disconnect (logout)
    });

    this.socket.on('connect_error', () => {
      // Connection failed; real-time notifications will not appear until reconnected
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket.removeAllListeners();
      this.socket = null;
    }
    this.store.clear();
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}
